import { API_SERVICE } from "@/services/api.service";
import { getEnvironmentConfig } from "@/jsFunctions/getEnvironmentConfig.js";
import { capacitorRequest, buildApiUrl } from '@/utils/capacitorHttp';

export default {
    namespaced: true,
    
    state: {
        // Maintenance request states
        submissionStatus: null,
        submissionError: null,
        isSubmitting: false,
        
        // Tenancy application states
        tenancyApplicationStatus: null,
        tenancyApplicationError: null,
        isTenancySubmitting: false,
        
        // Document upload states
        documentUploadStatus: null,
        documentUploadError: null,
        isUploadingDocuments: false,
        uploadProgress: {},
        
        // Contractor invoice states
        invoiceSubmissionStatus: null,
        invoiceSubmissionError: null,
        isSubmittingInvoice: false,
        
        // Prospect appointment states
        appointmentStatus: null,
        appointmentError: null,
        isSubmittingAppointment: false
    },
    
    mutations: {
        // Maintenance request mutations
        SET_SUBMISSION_STATUS(state, status) {
            state.submissionStatus = status;
        },
        SET_SUBMISSION_ERROR(state, error) {
            state.submissionError = error;
        },
        SET_IS_SUBMITTING(state, status) {
            state.isSubmitting = status;
        },
        
        // Tenancy application mutations
        SET_TENANCY_APPLICATION_STATUS(state, status) {
            state.tenancyApplicationStatus = status;
        },
        SET_TENANCY_APPLICATION_ERROR(state, error) {
            state.tenancyApplicationError = error;
        },
        SET_IS_TENANCY_SUBMITTING(state, status) {
            state.isTenancySubmitting = status;
        },
        
        // Document upload mutations
        SET_DOCUMENT_UPLOAD_STATUS(state, status) {
            state.documentUploadStatus = status;
        },
        SET_DOCUMENT_UPLOAD_ERROR(state, error) {
            state.documentUploadError = error;
        },
        SET_IS_UPLOADING_DOCUMENTS(state, status) {
            state.isUploadingDocuments = status;
        },
        SET_UPLOAD_PROGRESS(state, { fileName, progress }) {
            state.uploadProgress = {
                ...state.uploadProgress,
                [fileName]: progress
            };
        },
        CLEAR_UPLOAD_PROGRESS(state) {
            state.uploadProgress = {};
        },
        
        // Contractor invoice mutations
        SET_INVOICE_SUBMISSION_STATUS(state, status) {
            state.invoiceSubmissionStatus = status;
        },
        SET_INVOICE_SUBMISSION_ERROR(state, error) {
            state.invoiceSubmissionError = error;
        },
        SET_IS_SUBMITTING_INVOICE(state, status) {
            state.isSubmittingInvoice = status;
        },
        
        // Prospect appointment mutations
        SET_APPOINTMENT_STATUS(state, status) {
            state.appointmentStatus = status;
        },
        SET_APPOINTMENT_ERROR(state, error) {
            state.appointmentError = error;
        },
        SET_IS_SUBMITTING_APPOINTMENT(state, status) {
            state.isSubmittingAppointment = status;
        }
    },
    
    getters: {
        // Maintenance request getters
        submissionStatus: state => state.submissionStatus,
        submissionError: state => state.submissionError,
        isSubmitting: state => state.isSubmitting,
        
        // Tenancy application getters
        tenancyApplicationStatus: state => state.tenancyApplicationStatus,
        tenancyApplicationError: state => state.tenancyApplicationError,
        isTenancySubmitting: state => state.isTenancySubmitting,
        
        // Document upload getters
        documentUploadStatus: state => state.documentUploadStatus,
        documentUploadError: state => state.documentUploadError,
        isUploadingDocuments: state => state.isUploadingDocuments,
        uploadProgress: state => state.uploadProgress,
        
        // Contractor invoice getters
        invoiceSubmissionStatus: state => state.invoiceSubmissionStatus,
        invoiceSubmissionError: state => state.invoiceSubmissionError,
        isSubmittingInvoice: state => state.isSubmittingInvoice,
        
        // Prospect appointment getters
        appointmentStatus: state => state.appointmentStatus,
        appointmentError: state => state.appointmentError,
        isSubmittingAppointment: state => state.isSubmittingAppointment
    },
    
    actions: {
        // Document upload actions
        async uploadDocuments({ commit }, { files, objectOID, clientName }) {
            commit('SET_IS_UPLOADING_DOCUMENTS', true);
            commit('SET_DOCUMENT_UPLOAD_ERROR', null);
            commit('CLEAR_UPLOAD_PROGRESS');
            
            try {
                const uploadPromises = files.map(file => {
                    return new Promise((resolve, reject) => {
                        // Check if file has native base64 data (from native file picker)
                        if (file._nativeBase64) {
                            // Using native base64 data
                            
                            (async () => {
                                try {
                                    const documentObject = {
                                        DocumentName: file.name,
                                        MimeType: file.type || 'application/octet-stream',
                                        Content: file._nativeBase64,
                                        Size: file.size,
                                        Timestamp: Date.now()
                                    };

                                    // Set initial progress
                                    commit('SET_UPLOAD_PROGRESS', { 
                                        fileName: file.name, 
                                        progress: 0 
                                    });

                                    const envConfig = getEnvironmentConfig();
                                    // Sending native upload request
                                    
                                    const response = await API_SERVICE.request({
                                        method: 'POST',
                                        url: `${envConfig.baseURL}/v1/corporate/peopleportal/letmcletting/${clientName}/uploads/generic/${objectOID}`,
                                        data: documentObject,
                                        headers: {
                                            'api_key': envConfig.apiKey,
                                            'Content-Type': 'application/json'
                                        },
                                        onUploadProgress: (progressEvent) => {
                                            const percentCompleted = Math.round(
                                                (progressEvent.loaded * 100) / progressEvent.total
                                            );
                                            commit('SET_UPLOAD_PROGRESS', {
                                                fileName: file.name,
                                                progress: percentCompleted
                                            });
                                        }
                                    });
                                    
                                    // Native upload completed

                                    // Set final progress
                                    commit('SET_UPLOAD_PROGRESS', { 
                                        fileName: file.name, 
                                        progress: 100 
                                    });

                                    resolve(response.data);
                                } catch (error) {
                                    reject(error);
                                }
                            })();
                            return;
                        }
                        
                        // For regular browser files, use FileReader
                        const reader = new FileReader();
                        
                        reader.onload = async () => {
                            try {
                                const base64String = reader.result.split("base64,")[1];
                                // Clean the base64 string (remove any whitespace/newlines)
                                const cleanBase64 = base64String.replace(/[\s\n\r]/g, '');
                                
                                const documentObject = {
                                    DocumentName: file.name,
                                    MimeType: file.type || 'application/octet-stream',
                                    Content: cleanBase64,
                                    Size: file.size,
                                    Timestamp: Date.now()
                                };

                                // Set initial progress
                                commit('SET_UPLOAD_PROGRESS', { 
                                    fileName: file.name, 
                                    progress: 0 
                                });

                                const envConfig = getEnvironmentConfig();
                                // Sending browser upload request
                                
                                const response = await API_SERVICE.request({
                                    method: 'POST',
                                    url: `${envConfig.baseURL}/v1/corporate/peopleportal/letmcletting/${clientName}/uploads/generic/${objectOID}`,
                                    data: documentObject,
                                    headers: {
                                        'api_key': envConfig.apiKey,
                                        'Content-Type': 'application/json'
                                    },
                                    onUploadProgress: (progressEvent) => {
                                        const percentCompleted = Math.round(
                                            (progressEvent.loaded * 100) / progressEvent.total
                                        );
                                        commit('SET_UPLOAD_PROGRESS', {
                                            fileName: file.name,
                                            progress: percentCompleted
                                        });
                                    }
                                });
                                
                                // Browser upload completed

                                // Set final progress
                                commit('SET_UPLOAD_PROGRESS', { 
                                    fileName: file.name, 
                                    progress: 100 
                                });

                                resolve(response.data);
                            } catch (error) {
                                reject(error);
                            }
                        };

                        reader.onerror = () => reject(new Error('File reading failed'));
                        reader.readAsDataURL(file);
                    });
                });

                const results = await Promise.all(uploadPromises);
                commit('SET_DOCUMENT_UPLOAD_STATUS', results);
                return { success: true, data: results };
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'An error occurred while uploading documents';
                commit('SET_DOCUMENT_UPLOAD_ERROR', errorMessage);
                throw error;
            } finally {
                commit('SET_IS_UPLOADING_DOCUMENTS', false);
            }
        },

        // Maintenance request actions
        async submitMaintenanceRequest({ commit }, payload) {
            const { shortName, clientName, personID, requestData } = payload;
            const config = getEnvironmentConfig();
            
            commit('SET_IS_SUBMITTING', true);
            commit('SET_SUBMISSION_ERROR', null);
            
            try {
                const url = buildApiUrl(`/v1/corporate/peopleportal/${shortName}/${clientName}/uploads/${personID}/maintenance-request`, config);
                
                // Submitting maintenance request
                
                const response = await capacitorRequest({
                    method: 'POST',
                    url: url,
                    headers: {
                        'Content-Type': 'application/json',
                        'api_key': config.apiKey
                    },
                    data: {
                        Fault: requestData.Fault,
                        Message: requestData.Message,
                        PresenceRequested: requestData.PresenceRequested
                    }
                });

                commit('SET_SUBMISSION_STATUS', response.data);
                return response.data;
            } catch (error) {
                // Maintenance request submission error
                
                let errorMessage = 'An error occurred while submitting the maintenance request';
                
                if (error.response) {
                    errorMessage = error.response.data?.message || `Server error: ${error.response.status}`;
                } else if (error.request) {
                    errorMessage = 'Network error: Unable to reach the server. Please check your connection.';
                } else {
                    errorMessage = error.message || errorMessage;
                }
                
                commit('SET_SUBMISSION_ERROR', errorMessage);
                throw error;
            } finally {
                commit('SET_IS_SUBMITTING', false);
            }
        },

        // Tenancy application actions
        async submitTenancyApplication({ commit }, payload) {
            const { clientName, personID, tenancyID, applicationData } = payload;
            const config = getEnvironmentConfig();
            
            commit('SET_IS_TENANCY_SUBMITTING', true);
            commit('SET_TENANCY_APPLICATION_ERROR', null);
            
            try {
                const response = await API_SERVICE.request({
                    method: 'POST',
                    url: `${config.baseURL}/v1/corporate/peopleportal/letmcletting/${clientName}/uploads/${personID}/tenancy-application/${tenancyID}`,
                    headers: {
                        'Content-Type': 'application/json',
                        'api_key': config.apiKey
                    },
                    data: applicationData
                });

                commit('SET_TENANCY_APPLICATION_STATUS', response.data);
                return { success: true, data: response.data };
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'An error occurred while submitting the tenancy application';
                commit('SET_TENANCY_APPLICATION_ERROR', errorMessage);
                throw error;
            } finally {
                commit('SET_IS_TENANCY_SUBMITTING', false);
            }
        },

        // Contractor invoice submission
        async submitContractorInvoice({ commit }, payload) {
            const { clientName, contractorID, invoiceData } = payload;
            const config = getEnvironmentConfig();
            
            commit('SET_IS_SUBMITTING_INVOICE', true);
            commit('SET_INVOICE_SUBMISSION_ERROR', null);
            
            try {
                const response = await API_SERVICE.request({
                    method: 'POST',
                    url: `${config.baseURL}/v1/corporate/peopleportal/letmcletting/${clientName}/uploads/${contractorID}/submitted-invoices/submit`,
                    headers: {
                        'Content-Type': 'application/json',
                        'api_key': config.apiKey
                    },
                    data: {
                        MaitenanceJobID: invoiceData.MaitenanceJobID,
                        PropertyID: invoiceData.PropertyID,
                        Amount: invoiceData.Amount,
                        Description: invoiceData.Description,
                        Documents: invoiceData.Documents
                    }
                });

                commit('SET_INVOICE_SUBMISSION_STATUS', response.data);
                return { success: true, data: response.data };
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'An error occurred while submitting the invoice';
                commit('SET_INVOICE_SUBMISSION_ERROR', errorMessage);
                throw error;
            } finally {
                commit('SET_IS_SUBMITTING_INVOICE', false);
            }
        },

        // Prospect appointment submission
        async submitProspectAppointment({ commit }, payload) {
            const { clientName, prospectID, appointmentData } = payload;
            const config = getEnvironmentConfig();
            
            commit('SET_IS_SUBMITTING_APPOINTMENT', true);
            commit('SET_APPOINTMENT_ERROR', null);
            
            try {
                const response = await API_SERVICE.request({
                    method: 'POST',
                    url: `${config.baseURL}/v1/corporate/peopleportal/letmcletting/${clientName}/prospect/tenant/${prospectID}/appointment`,
                    headers: {
                        'Content-Type': 'application/json',
                        'api_key': config.apiKey
                    },
                    data: appointmentData
                });

                commit('SET_APPOINTMENT_STATUS', response.data);
                return { success: true, data: response.data };
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'An error occurred while booking the appointment';
                commit('SET_APPOINTMENT_ERROR', errorMessage);
                throw error;
            } finally {
                commit('SET_IS_SUBMITTING_APPOINTMENT', false);
            }
        },

        // Quick create appointments for prospects
        async createQuickAppointments({ commit }, payload) {
            const { shortName, clientName, prospectID } = payload;
            const config = getEnvironmentConfig();
            
            commit('SET_IS_SUBMITTING_APPOINTMENT', true);
            commit('SET_APPOINTMENT_ERROR', null);
            
            try {
                const response = await API_SERVICE.request({
                    method: 'GET',
                    url: `${config.baseURL}/v1/corporate/peopleportal/${shortName}/${clientName}/prospects/${prospectID}/quickCreateAppointments`,
                    headers: {
                        'api_key': config.apiKey
                    }
                });

                commit('SET_APPOINTMENT_STATUS', response.data);
                return { success: true, data: response.data };
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'An error occurred while creating quick appointments';
                commit('SET_APPOINTMENT_ERROR', errorMessage);
                throw error;
            } finally {
                commit('SET_IS_SUBMITTING_APPOINTMENT', false);
            }
        },

        // Status clearing actions
        clearSubmissionStatus({ commit }) {
            commit('SET_SUBMISSION_STATUS', null);
            commit('SET_SUBMISSION_ERROR', null);
        },

        clearTenancyApplicationStatus({ commit }) {
            commit('SET_TENANCY_APPLICATION_STATUS', null);
            commit('SET_TENANCY_APPLICATION_ERROR', null);
        },

        clearDocumentUploadStatus({ commit }) {
            commit('SET_DOCUMENT_UPLOAD_STATUS', null);
            commit('SET_DOCUMENT_UPLOAD_ERROR', null);
            commit('CLEAR_UPLOAD_PROGRESS');
        },

        clearInvoiceSubmissionStatus({ commit }) {
            commit('SET_INVOICE_SUBMISSION_STATUS', null);
            commit('SET_INVOICE_SUBMISSION_ERROR', null);
        },

        clearAppointmentStatus({ commit }) {
            commit('SET_APPOINTMENT_STATUS', null);
            commit('SET_APPOINTMENT_ERROR', null);
        }
    }
};